/**
 * Shared reports chrome (S11.1): the formula caption (subtitle help per Board
 * E), the CSV-export toolbar button, and the per-report sync note. All copy
 * arrives via the `reports` namespace.
 */

import { Button, Typography, theme } from 'antd';
import { DownloadOutlined, FunctionOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import { controlHeightTokens, radiusTokens } from '@/styles/tokens';

const { Text } = Typography;

/** Formula caption — the "銷售金額 − 採購成本 = 毛利" subtitle help. */
export const FormulaCaption = ({ text }: { text: string }) => {
  const { token } = theme.useToken();
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: token.marginXS,
        padding: `${token.paddingXXS}px ${token.paddingSM}px`,
        backgroundColor: token.colorFillQuaternary,
        color: token.colorTextSecondary,
        borderRadius: radiusTokens.control,
        fontSize: token.fontSizeSM,
      }}
    >
      <FunctionOutlined style={{ color: token.colorPrimary }} />
      <span>{text}</span>
    </span>
  );
};

/** CSV export button (stub — serializes the on-screen rows client-side). */
export const CsvExportButton = ({
  onExport,
  disabled,
}: {
  onExport: () => void;
  disabled?: boolean;
}) => {
  const { t } = useTranslation('reports');
  return (
    <Button
      icon={<DownloadOutlined />}
      size="large"
      style={{ height: `${controlHeightTokens.controlHeightXL}px` }}
      onClick={onExport}
      disabled={disabled}
    >
      {t('reports.actions.exportCsv')}
    </Button>
  );
};

/** Formula caption on the left, CSV export on the right — the report toolbar. */
export const ReportToolbar = ({
  formula,
  onExport,
  exportDisabled,
}: {
  formula: string;
  onExport: () => void;
  exportDisabled?: boolean;
}) => {
  const { token } = theme.useToken();
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: token.marginSM,
        marginBottom: token.marginMD,
        flexWrap: 'wrap',
      }}
    >
      <FormulaCaption text={formula} />
      <CsvExportButton onExport={onExport} disabled={exportDisabled} />
    </div>
  );
};

/** 資料同步於 {time} — the per-report freshness note. */
export const ReportSyncNote = ({ syncedAt }: { syncedAt?: string }) => {
  const { t } = useTranslation('reports');
  const { token } = theme.useToken();
  return (
    <Text
      type="secondary"
      style={{
        display: 'block',
        marginTop: token.marginMD,
        fontSize: token.fontSizeSM,
        fontVariantNumeric: 'tabular-nums',
      }}
    >
      {t('reports.syncedAt', {
        time: syncedAt ? dayjs(syncedAt).format('YYYY-MM-DD HH:mm') : '-',
      })}
    </Text>
  );
};
